import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageSquare, Bell, Globe, Trash2, Plus } from 'lucide-react';
import { notificationsApi } from '../services/api';
import { Card, SectionHeader, Loader } from '../components/ui';
import toast from 'react-hot-toast';

const BLUE = '#4a9eff';
const GREEN = '#22c55e';
const RED = '#ff5c5c';

const EVENT_KINDS = [
  { id: 'COST_BREACH',       label: 'Budget breach',        desc: 'Monthly spend crosses budget threshold' },
  { id: 'BUDGET_THRESHOLD',  label: 'Budget threshold',     desc: '75% / 90% budget warnings' },
  { id: 'RECO_READY',        label: 'New recommendation',   desc: 'A new optimization opportunity is found' },
  { id: 'AUTONOMY_APPLIED',  label: 'Autonomy applied',     desc: 'A recommendation was auto-applied' },
  { id: 'INCIDENT_OPENED',   label: 'Incident opened',      desc: 'AI SRE detects a new incident' },
  { id: 'ANOMALY_DETECTED',  label: 'Cost anomaly',         desc: 'Unusual cost spike detected' },
  { id: 'AUDIT_ALERT',       label: 'Security audit alert', desc: 'Suspicious login or role change' },
];

const CHANNELS = [
  { id: 'EMAIL',    icon: Mail,          label: 'Email' },
  { id: 'SLACK',    icon: MessageSquare, label: 'Slack' },
  { id: 'WEBHOOK',  icon: Globe,         label: 'Webhook' },
  { id: 'INAPP',    icon: Bell,          label: 'In-app' },
];

export default function NotificationsSettingsPage() {
  const qc = useQueryClient();
  const { data: subs = [], isLoading } = useQuery({
    queryKey: ['notif-subs'],
    queryFn: () => notificationsApi.listSubscriptions().then((r) => r.data),
    retry: false,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [newEvent, setNewEvent] = useState(EVENT_KINDS[0].id);
  const [newChannel, setNewChannel] = useState('EMAIL');
  const [newTarget, setNewTarget] = useState('');
  const [newSeverity, setNewSeverity] = useState('INFO');

  const subscribe = useMutation({
    mutationFn: () => notificationsApi.subscribe({
      eventKind: newEvent, channel: newChannel, channelTarget: newTarget || null, minSeverity: newSeverity,
    }),
    onSuccess: () => {
      toast.success('Subscription saved');
      qc.invalidateQueries({ queryKey: ['notif-subs'] });
      setShowAdd(false); setNewTarget('');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });
  const unsubscribe = useMutation({
    mutationFn: (id) => notificationsApi.unsubscribe(id),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['notif-subs'] }); },
  });

  if (isLoading) return <Loader />;

  return (
    <div>
      <SectionHeader title="Notifications"
        subtitle="Choose which events reach you, and how. Every subscription is per-user and per-channel." />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowAdd(!showAdd)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 8,
            fontSize: 13, fontWeight: 700, background: BLUE, color: '#fff', border: 'none', cursor: 'pointer' }}>
          <Plus size={14} /> Add subscription
        </button>
      </div>

      {showAdd && (
        <Card style={{ padding: 20, marginBottom: 20, borderLeft: `4px solid ${BLUE}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Event" value={newEvent} onChange={setNewEvent} options={EVENT_KINDS.map((e) => [e.id, e.label])} />
            <Select label="Channel" value={newChannel} onChange={setNewChannel} options={CHANNELS.map((c) => [c.id, c.label])} />
          </div>
          {newChannel !== 'INAPP' && (
            <Field label={targetLabel(newChannel)} value={newTarget} onChange={setNewTarget}
              placeholder={targetPlaceholder(newChannel)} />
          )}
          <Select label="Minimum severity" value={newSeverity} onChange={setNewSeverity}
            options={[['INFO', 'INFO – all events'], ['WARN', 'WARN – warn and above'], ['CRITICAL', 'CRITICAL – critical only']]} />
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={() => subscribe.mutate()} disabled={subscribe.isLoading}
              style={{ padding: '9px 16px', borderRadius: 8, background: GREEN, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Save</button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '9px 16px', borderRadius: 8, background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer' }}>Cancel</button>
          </div>
        </Card>
      )}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px 60px', padding: '12px 16px',
                      background: 'rgba(74,158,255,0.06)', fontSize: 11, fontWeight: 700,
                      color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <div>Event</div><div>Channel</div><div>Target</div><div>Min sev.</div><div></div>
        </div>
        {subs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            No subscriptions yet. Add one to start receiving notifications.
          </div>
        ) : subs.map((s) => {
          const evt = EVENT_KINDS.find((e) => e.id === s.eventKind) || { label: s.eventKind, desc: '' };
          const ch  = CHANNELS.find((c) => c.id === s.channel) || { icon: Bell, label: s.channel };
          const Icon = ch.icon;
          return (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px 60px',
                          padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.05)',
                          fontSize: 13, alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, color: '#fff' }}>{evt.label}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{evt.desc}</div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon size={13} color={BLUE} /> {ch.label}
              </div>
              <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.channelTarget || '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: sevColor(s.minSeverity) }}>{s.minSeverity}</div>
              <div>
                <button onClick={() => { if (window.confirm('Remove this subscription?')) unsubscribe.mutate(s.id); }}
                  style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', padding: 4 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', fontFamily: 'ui-monospace, monospace', boxSizing: 'border-box' }} />
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', boxSizing: 'border-box' }}>
        {options.map(([v, l]) => <option key={v} value={v} style={{ background: '#0d1527', color: '#fff' }}>{l}</option>)}
      </select>
    </div>
  );
}
const targetLabel = (ch) => ({
  EMAIL: 'Email address (blank → account email)',
  SLACK: 'Slack webhook URL',
  WEBHOOK: 'Webhook URL (HTTPS)',
}[ch] || 'Target');
const targetPlaceholder = (ch) => ({
  EMAIL: 'alerts@yourcompany.com',
  SLACK: 'https://hooks.slack.com/services/T00…',
  WEBHOOK: 'https://your-endpoint.example.com/smalblu-hook',
}[ch] || '');
const sevColor = (s) => ({ CRITICAL: RED, WARN: '#ffb800', INFO: BLUE }[s] || BLUE);
